import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTran, useUnsavedChanges } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";
import { PATHS } from "@/lib/router";
import { useAuthStore } from "@/features/auth";
import { useOrganizationStore } from "@/features/organization";
import { esgService, masterDataService } from "@/features/esg/services";
import { showToast } from "@/lib/utils/toast";
import { PageHeader, SearchableSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import SearchableMultiSelect from "../components/masterData/SearchableMultiSelect";
import AssignMasterDataDialog from "../components/masterData/AssignMasterDataDialog";
import { ListChecks } from "lucide-react";
import { Input } from "@/components/ui/input";
import { reportingBoundaryService } from "@/features/reporting-boundary/services/reporting-boundary.services";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";
import { formatReportingYearPeriod } from "../utils/period-constants";

const CATEGORY_STYLES = {
  General: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    badge: "bg-purple-600",
  },
  Environmental: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    badge: "bg-emerald-600",
  },
  Social: { bg: "bg-blue-100", text: "text-blue-700", badge: "bg-blue-600" },
  Governance: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    badge: "bg-amber-600",
  },
  Other: { bg: "bg-slate-100", text: "text-slate-700", badge: "bg-slate-600" },
};

const DIALOG_CATEGORY_ORDER = [
  "General",
  "Environmental",
  "Social",
  "Governance",
];

const normalizeDialogCategory = (category) => {
  const normalized = String(category || "")
    .trim()
    .toLowerCase();
  if (normalized.startsWith("gen")) return "General";
  if (normalized.startsWith("env")) return "Environmental";
  if (normalized.startsWith("soc")) return "Social";
  if (normalized.startsWith("gov")) return "Governance";
  return null;
};

const getDisclosureCategory = (item) =>
  normalizeDialogCategory(item?.category) ||
  String(item?.category || "").trim();

const sortDialogCategories = (categories) =>
  [...categories].sort((categoryA, categoryB) => {
    const indexA = DIALOG_CATEGORY_ORDER.indexOf(categoryA);
    const indexB = DIALOG_CATEGORY_ORDER.indexOf(categoryB);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

const disclosureMatchesSearch = (item, searchTerm) => {
  const query = String(searchTerm || "")
    .trim()
    .toLowerCase();

  if (!query) return true;

  const subEntryFields = Array.isArray(item?.subEntries)
    ? item.subEntries.flatMap((entry) => [
        entry?.code,
        entry?.subEntryCode,
        entry?.title,
      ])
    : [];

  return [
    item?.code,
    item?.title,
    item?.topic,
    item?.framework,
    item?.category,
    getDisclosureCategory(item),
    ...subEntryFields,
  ].some((value) =>
    String(value || "")
      .toLowerCase()
      .includes(query),
  );
};

const CATEGORY_GRID_CLASS_BY_COUNT = {
  1: "xl:grid-cols-1",
  2: "xl:grid-cols-2",
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
};

const getCategoryGridClass = (count) =>
  CATEGORY_GRID_CLASS_BY_COUNT[Math.min(Math.max(count, 1), 4)];

const getCurrentCompanyId = (user) =>
  user?.companyId || user?.company?._id || user?.company?.id || user?.orgId;

const normalizeFrameworkCodes = (frameworkCodes = []) =>
  Array.from(
    new Set(
      (Array.isArray(frameworkCodes) ? frameworkCodes : [])
        .map((item) => String(item || "").trim())
        .filter(Boolean),
    ),
  );

const sortReportingYearsDescending = (reportingYears = []) =>
  [...reportingYears].sort((yearA = {}, yearB = {}) => {
    const fields = ["startYear", "endYear", "startMonth", "endMonth"];

    for (const field of fields) {
      const valueA = Number(yearA[field]) || 0;
      const valueB = Number(yearB[field]) || 0;
      if (valueA !== valueB) return valueB - valueA;
    }

    const createdAtA = yearA.createdAt
      ? new Date(yearA.createdAt).getTime()
      : 0;
    const createdAtB = yearB.createdAt
      ? new Date(yearB.createdAt).getTime()
      : 0;
    return createdAtB - createdAtA;
  });

const normalizeFrameworkOption = (framework = {}) => {
  const code =
    framework.code ||
    framework.framework ||
    framework.value ||
    framework.id ||
    framework._id;
  if (!code) return null;

  return {
    code: String(code).trim(),
    name: String(framework.name || framework.label || code).trim(),
  };
};

const mapDisclosureOption = (item = {}) => ({
  id: item._id || item.id,
  code: item.code,
  title: item.title,
  topic: item.topic || null,
  framework: item.framework,
  category: item.category,
  inputType: item.inputType || "simple",
  unitOptions: Array.isArray(item.unitOptions) ? item.unitOptions : [],
  subEntries: Array.isArray(item.subEntries) ? item.subEntries : [],
  isCalculated: Boolean(item.isCalculated),
  formula: item.formula || null,
  formulaScope: item.formulaScope || "code",
  defaultUnit: item.defaultUnit || null,
});

const AssignDisclosureGridSkeleton = () => (
  <div className="grid grid-cols-1 gap-4 flex-1 min-h-0 overflow-hidden xl:grid-cols-4">
    {Array.from({ length: 4 }).map((_, cardIndex) => (
      <div
        key={`assign-disclosure-skeleton-${cardIndex}`}
        className="my-2 flex h-full min-h-0 flex-col space-y-3 rounded-radius border border-border bg-card p-3"
      >
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
        <div className="min-h-[18rem] flex-1 space-y-2 pr-1 sm:min-h-[20rem] xl:min-h-[50vh] xl:max-h-[50vh]">
          {Array.from({ length: 6 }).map((__, rowIndex) => (
            <div
              key={`assign-disclosure-skeleton-${cardIndex}-${rowIndex}`}
              className="rounded-md border border-border p-2"
            >
              <div className="flex items-center gap-x-2">
                <Skeleton className="h-4 w-4 rounded-sm" />
                <div className="w-full space-y-1">
                  <Skeleton className="h-4 w-[40%]" />
                  <Skeleton className="h-3 w-[80%]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const AssignDisclosureSelectionCard = ({
  category,
  disclosures,
  selectedDisclosureIds,
  disclosuresLoading,
  onToggleDisclosure,
  onSelectAllByCategory,
  onUnselectAllByCategory,
  getFrameworkLabel,
  t,
}) => {
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.Other;
  const initial = category?.charAt(0)?.toUpperCase() || "?";
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const accordionGroups = useMemo(() => {
    const groupMap = new Map();

    const getBaseCode = (code) => {
      const normalized = String(code || "").trim();
      if (!normalized.includes("-")) return normalized;
      const parts = normalized.split("-");
      return parts.slice(0, -1).join("-").trim() || normalized;
    };

    disclosures.forEach((item) => {
      const code = String(item.code || "").trim();
      const topic = String(item.topic || "").trim();
      const framework = String(item.framework || "").trim();
      if (!code && !topic) return;

      const baseCode = topic || getBaseCode(code);
      const groupKey = `${framework || "UNKNOWN"}::${baseCode}`;
      const hasChildPattern =
        (topic && code && topic !== code) || code.includes("-");
      const parentTitleCandidate = topic || item.title || code || "-";

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          key: groupKey,
          topic: baseCode,
          framework,
          parentTitle: parentTitleCandidate,
          children: [],
        });
      }

      const group = groupMap.get(groupKey);
      if (!group.parentTitle || group.parentTitle === "-") {
        group.parentTitle = parentTitleCandidate;
      }

      if (hasChildPattern) {
        group.children.push({
          id: item.id,
          code,
          title: item.title || "-",
          selectable: true,
        });
        return;
      }

      if (Array.isArray(item.subEntries) && item.subEntries.length > 0) {
        // Use subEntries only when there are no explicit child disclosures.
        if (group.children.length === 0) {
          item.subEntries.forEach((entry, index) => {
            group.children.push({
              id: item.id,
              key: `${item.id}-sub-${index}`,
              code:
                entry?.code ||
                entry?.subEntryCode ||
                `${baseCode}-${index + 1}`,
              title: entry?.title || "-",
              selectable: true,
            });
          });
        }
        return;
      }

      group.children.push({
        id: item.id,
        key: item.id,
        code,
        title: item.title || "-",
        selectable: true,
      });
    });

    return Array.from(groupMap.values());
  }, [disclosures]);

  const isSelectedAllByCategory =
    disclosures.length > 0 &&
    disclosures.every((d) => selectedDisclosureIds.includes(d.id));

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  return (
    <div className="my-2 flex h-full min-h-0 flex-col space-y-3 rounded-radius border border-border bg-card p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${style.bg} ${style.text}`}
          >
            {initial}
          </span>
          <div className="flex flex-1 flex-col">
            <p className="text-sm font-semibold">{t(category)}</p>
            <span className="text-xs text-muted-foreground">
              {accordionGroups.reduce(
                (total, group) => total + (group.children?.length ?? 0),
                0,
              )}{" "}
              {t("metric(s)")}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          className="text-primary text-xs"
          onClick={() =>
            isSelectedAllByCategory
              ? onUnselectAllByCategory(category)
              : onSelectAllByCategory(category)
          }
          size="sm"
          disabled={disclosures.length === 0}
        >
          {t(isSelectedAllByCategory ? "Unselect All" : "Select All")}
        </Button>
      </div>

      <div className="min-h-[18rem] flex-1 space-y-2 overflow-y-auto pr-1 sm:min-h-[20rem] xl:min-h-[50vh] xl:max-h-[50vh]">
        {disclosuresLoading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`${category}-skeleton-${index}`}
              className="rounded-md border-border border p-2"
            >
              <div className="flex items-center gap-x-2">
                <Skeleton className="h-4 w-4 rounded-sm" />
                <div className="w-full space-y-1">
                  <Skeleton className="h-4 w-[90px]" />
                  <Skeleton className="h-3 w-[220px]" />
                </div>
              </div>
            </div>
          ))
        ) : disclosures.length === 0 ? (
          <div className="rounded-md border-border border p-4 text-xs text-muted-foreground">
            {t("No disclosures found.")}
          </div>
        ) : (
          accordionGroups.map((group) => {
            const isExpanded = !collapsedGroups.has(group.key);
            return (
              <div key={group.key} className="rounded-md border-border border">
                <button
                  type="button"
                  className={`p-2 flex items-center justify-between cursor-pointer hover:bg-muted ${isExpanded ? "border-b" : ""} border-border w-full text-left bg-primary/3`}
                  aria-expanded={isExpanded}
                  onClick={() => toggleGroup(group.key)}
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm text-foreground whitespace-nowrap truncate uppercase">
                        {group.parentTitle}
                      </p>
                    </div>
                    {group.framework ? (
                      <p className="text-sky-600 text-xs">
                        {getFrameworkLabel
                          ? getFrameworkLabel(group.framework)
                          : group.framework}
                      </p>
                    ) : null}
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-foreground" />
                  ) : (
                    <ChevronRight size={16} className="text-foreground" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-2 space-y-1">
                    {group.children.map((child, index) => {
                      const checkboxId = `${child.id}-${index}`;
                      return (
                        <div
                          key={child.key || `${group.key}-sub-${index}`}
                          className="flex text-xs items-center gap-x-2 rounded-md p-1"
                        >
                          <Checkbox
                            id={checkboxId}
                            className="shrink-0"
                            checked={selectedDisclosureIds.includes(child.id)}
                            onCheckedChange={(checked) =>
                              onToggleDisclosure(child.id, Boolean(checked))
                            }
                          />
                          <label
                            htmlFor={checkboxId}
                            className="flex flex-col min-w-0 flex-1 flex-nowrap text-xs gap-x-2 overflow-hidden cursor-pointer"
                          >
                            <p className="font-semibold whitespace-nowrap">
                              {child.code}
                            </p>
                            <p className="text-muted-foreground">
                              {child.title}
                            </p>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const AssignMasterData = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user, isJJSEA } = useAuthStore();
  const companyId = getCurrentCompanyId(user) || "";
  const {
    fetchHierarchy,
    tree: organizationTree,
    loading: organizationLoading,
  } = useOrganizationStore();

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);

  const [reportingYears, setReportingYears] = useState([]);
  const [
    frameworksFromSelectedReportingYear,
    setFrameworksFromSelectedReportingYear,
  ] = useState([]);

  const [frameworks, setFrameworks] = useState([]);

  const [frameworkSelectionReady, setFrameworkSelectionReady] = useState(false);

  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [reportingYearsLoading, setReportingYearsLoading] = useState(true);
  const [frameworksLoading, setFrameworksLoading] = useState(true);

  // const formatReportingPeriod = (reportingYear) => {
  //   if (!reportingYear) return "";

  //   return `${reportingYear.startYear} (${MONTHS[reportingYear.startMonth - 1]} ${
  //     reportingYear.startYear
  //   } - ${MONTHS[reportingYear.endMonth - 1]} ${reportingYear.endYear} )`;
  // };

  const reportingYearOptions = useMemo(
    () =>
      reportingYears.map((item) => ({
        value: item._id,
        label: formatReportingYearPeriod(item),
      })),
    [reportingYears],
  );

  const handleReportingYearChange = (reportingYearId) => {
    const reportingYear = reportingYears.find(
      (item) => String(item._id) === String(reportingYearId),
    );

    setSelectedReportingYear(reportingYear || null);

    const frameworkCodes = normalizeFrameworkCodes(
      reportingYear?.frameworkCodes || [],
    );
    setYear(reportingYear?.startYear || undefined);
    setFrameworksFromSelectedReportingYear(frameworkCodes);
    setFrameworkSelectionReady(true);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchReportingYears = async () => {
      setReportingYearsLoading(true);
      try {
        const data = await loadReportingYears();
        const sortedReportingYears = sortReportingYearsDescending(data);
        if (!isMounted) return;

        const defaultReportingYear = sortedReportingYears[0] || null;
        const defaultFrameworkCodes = normalizeFrameworkCodes(
          defaultReportingYear?.frameworkCodes || [],
        );

        setReportingYears(sortedReportingYears);
        setSelectedReportingYear(defaultReportingYear);
        setYear(defaultReportingYear?.startYear || undefined);
        setFrameworksFromSelectedReportingYear(defaultFrameworkCodes);
        setFrameworkSelectionReady(defaultFrameworkCodes.length > 0);
      } finally {
        if (isMounted) {
          setReportingYearsLoading(false);
        }
      }
    };

    void fetchReportingYears();
    return () => {
      isMounted = false;
    };
  }, []);

  const frameworkNameByCode = useMemo(
    () =>
      new Map(
        availableFrameworks
          .map(normalizeFrameworkOption)
          .filter(Boolean)
          .map((framework) => [framework.code, framework.name]),
      ),
    [availableFrameworks],
  );

  const getFrameworkLabel = useCallback(
    (frameworkCode) => {
      const code = String(frameworkCode || "").trim();
      return frameworkNameByCode.get(code) || code;
    },
    [frameworkNameByCode],
  );

  const frameworkOptions = useMemo(
    () =>
      frameworksFromSelectedReportingYear.map((frameworkCode) => {
        const label = getFrameworkLabel(frameworkCode);
        return {
          value: frameworkCode,
          label,
          meta: label === frameworkCode ? "" : frameworkCode,
        };
      }),
    [frameworksFromSelectedReportingYear, getFrameworkLabel],
  );

  const [year, setYear] = useState();
  const [selectedDisclosureIds, setSelectedDisclosureIds] = useState([]);
  const [organizationMode, setOrganizationMode] = useState("specific");
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [allDisclosureOptions, setAllDisclosureOptions] = useState([]);
  const [loadingAllDisclosures, setLoadingAllDisclosures] = useState(false);
  const [saving, setSaving] = useState(false);
  const allOrganizations = organizationMode === "all";
  const [dialogOpen, setDialogOpen] = useState(false);

  const isDirty = selectedDisclosureIds.length > 0;
  const { showDialog, confirmDiscard, cancelDiscard, bypass } =
    useUnsavedChanges(isDirty);

  const hasGeneralDisclosure = useMemo(
    () =>
      allDisclosureOptions.some(
        (d) =>
          selectedDisclosureIds.includes(d.id) &&
          normalizeDialogCategory(d.category) === "General",
      ),
    [selectedDisclosureIds, allDisclosureOptions],
  );

  useEffect(() => {
    let isMounted = true;

    const loadFrameworks = async () => {
      setFrameworksLoading(true);
      try {
        const data = await reportingBoundaryService.getFrameworks({
          throwOnError: false,
        });
        if (isMounted) {
          setAvailableFrameworks(
            Array.isArray(data?.frameworks) ? data.frameworks : [],
          );
        }
      } catch {
        if (isMounted) {
          setAvailableFrameworks([]);
        }
      } finally {
        if (isMounted) {
          setFrameworksLoading(false);
        }
      }
    };

    void loadFrameworks();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (frameworkOptions.length === 0) {
      setFrameworkSelectionReady(false);
      setFrameworks([]);
      return;
    }
    setFrameworkSelectionReady(true);
  }, [frameworkOptions, frameworkSelectionReady]);

  useEffect(() => {
    if (organizationTree.length > 0 || organizationLoading) return;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy, organizationLoading, organizationTree.length]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      setLoadingAllDisclosures(true);
      try {
        if (!frameworkSelectionReady) return;

        const selectedFrameworks =
          frameworks.length > 0
            ? frameworks // use what user selected
            : frameworkOptions.map((item) => item.value); // treat as all

        if (selectedFrameworks.length === 0) {
          if (isMounted) setAllDisclosureOptions([]);
          return;
        }

        const data = await esgService.getDisclosures(
          {
            search: "",
            framework: selectedFrameworks.join(","),
            category: "All",
            page: 1,
            limit: 10000,
          },
          { throwOnError: false },
        );

        const disclosures = Array.isArray(data?.disclosures)
          ? data.disclosures.map(mapDisclosureOption).filter((item) => item.id)
          : [];

        if (isMounted) {
          setAllDisclosureOptions(disclosures);
        }
      } catch (error) {
        if (isMounted) {
          setAllDisclosureOptions([]);
        }
        showToast.error(error?.message || t("Failed to load disclosures."));
      } finally {
        if (isMounted) {
          setLoadingAllDisclosures(false);
        }
      }
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [frameworkOptions, frameworkSelectionReady, frameworks, t]);

  useEffect(() => {
    if (!isJJSEA) return;

    // Only initialize if nothing is selected
    if (frameworks.length > 0) return;

    const jjGroup = frameworkOptions.find(
      (option) => option.value === "JJ-Group",
    );

    if (!jjGroup) return;

    setFrameworks([jjGroup.value]);
  }, [isJJSEA, frameworkOptions, frameworks]);

  const organizationFilteredDisclosures = useMemo(() => {
    if (allOrganizations || selectedOrganizations.length === 0) {
      return allDisclosureOptions;
    }
    return allDisclosureOptions.filter((item) => {
      if (!item.organizationId && !item.organization) return true;
      return selectedOrganizations.includes(
        item.organizationId || item.organization,
      );
    });
  }, [allOrganizations, selectedOrganizations, allDisclosureOptions]);

  const categoryNames = useMemo(() => {
    const categories = new Set();

    organizationFilteredDisclosures.forEach((item) => {
      const category = getDisclosureCategory(item);

      if (!category) return;

      if (!selectedReportingYear?.isCompanySelected && category === "General") {
        return;
      }

      categories.add(category);
    });

    return sortDialogCategories(Array.from(categories));
  }, [
    organizationFilteredDisclosures,
    selectedReportingYear?.isCompanySelected,
  ]);
  const filteredDisclosures = useMemo(() => {
    if (!searchTerm) return organizationFilteredDisclosures;
    return organizationFilteredDisclosures.filter((item) =>
      disclosureMatchesSearch(item, searchTerm),
    );
  }, [organizationFilteredDisclosures, searchTerm]);

  const groupedByCategory = useMemo(() => {
    const grouped = categoryNames.reduce((acc, category) => {
      acc[category] = [];
      return acc;
    }, {});

    filteredDisclosures.forEach((item) => {
      const category = getDisclosureCategory(item);
      if (!category) return;

      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });

    return grouped;
  }, [categoryNames, filteredDisclosures]);

  const selectedCount = selectedDisclosureIds.length;
  const categoryGridClass = getCategoryGridClass(categoryNames.length);
  const initialDisclosuresLoading =
    categoryNames.length === 0 &&
    (reportingYearsLoading || frameworksLoading || loadingAllDisclosures);

  const toggleDisclosureSelection = (id, checked) => {
    setSelectedDisclosureIds((prev) => {
      if (checked) return [...new Set([...prev, id])];
      return prev.filter((itemId) => itemId !== id);
    });
  };

  const selectAllByCategory = (category) => {
    const categoryIds = (groupedByCategory[category] || []).map(
      (item) => item.id,
    );
    setSelectedDisclosureIds((prev) => [...new Set([...prev, ...categoryIds])]);
  };

  const unselectAllByCategory = (category) => {
    const categoryIdSet = new Set(
      (groupedByCategory[category] || []).map((item) => item.id),
    );
    setSelectedDisclosureIds((prev) =>
      prev.filter((id) => !categoryIdSet.has(id)),
    );
  };

  const handleSave = async () => {
    const disclosureIds = Array.from(new Set(selectedDisclosureIds));
    const organizationIds = allOrganizations
      ? []
      : Array.from(new Set(selectedOrganizations));

    if (disclosureIds.length === 0) {
      showToast.error(t("Please select at least one disclosure."));
      return;
    }
    if (
      !hasGeneralDisclosure &&
      !allOrganizations &&
      organizationIds.length === 0
    ) {
      showToast.error(t("Please select at least one organization."));
      return;
    }
    if (!companyId) {
      showToast.error(t("Company is required."));
      return;
    }

    const payload = {
      disclosureIds,
      organizationIds,
      isAllOrganization: allOrganizations,
      companyId,
      year: Number.parseInt(year, 10),
      period: {
        startYear: selectedReportingYear?.startYear,
        startMonth: selectedReportingYear?.startMonth,
        endYear: selectedReportingYear?.endYear,
        endMonth: selectedReportingYear?.endMonth,
      },
    };

    setSaving(true);
    try {
      const savePromise = masterDataService.saveByDisclosures(payload);
      showToast.promise(savePromise, {
        loading: t("Saving master data..."),
        success: t("Master data assigned successfully."),
        error: (error) => error?.message || t("Failed to assign master data."),
      });
      await savePromise;
      bypass();
      navigate(PATHS.ESG_MASTER_DATA);
    } catch {
      // Error toast is handled by showToast.promise above.
    } finally {
      setSaving(false);
    }
  };

  const reportingYearOrganizationIds = useMemo(() => {
    const orgIds = (selectedReportingYear?.organizations || []).map((org) =>
      String(org._id),
    );

    if (
      selectedReportingYear?.isCompanySelected &&
      companyId &&
      !selectedReportingYear?.isAllOrganization
    ) {
      orgIds.push(String(companyId));
    }

    return Array.from(new Set(orgIds));
  }, [selectedReportingYear, companyId]);

  const reportingYearOrganizationTree = useMemo(() => {
    if (!selectedReportingYear) return [];

    if (selectedReportingYear.isAllOrganization) {
      return organizationTree;
    }

    const allowedSet = new Set(reportingYearOrganizationIds.map(String));

    const getNodeId = (node) => String(node.id || node._id || node.key);

    const filterNode = (node) => {
      const children = (node.children || []).map(filterNode).filter(Boolean);

      const nodeId = getNodeId(node);
      const isAllowed = allowedSet.has(nodeId);

      // show node if it is allowed OR any child is allowed
      if (isAllowed || children.length > 0) {
        return {
          ...node,
          children,
        };
      }

      return null;
    };

    return (organizationTree || []).map(filterNode).filter(Boolean);
  }, [organizationTree, selectedReportingYear, reportingYearOrganizationIds]);

  return (
    <div className="space-y-2">
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
      <button
        type="button"
        onClick={() => navigate(PATHS.ESG_MASTER_DATA)}
        className="mb-1 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("Back")}
      </button>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <PageHeader
          title={t("Define Disclosure")}
          icon={ListChecks}
          description={t(
            "Select KPI discloures to assign to an organization for the reporting year.",
          )}
        />
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setSelectedDisclosureIds([])}
            disabled={saving}
          >
            {t("Clear")}
          </Button>
          <Button
            type="button"
            onClick={() => setDialogOpen(true)}
            disabled={selectedDisclosureIds.length === 0 || saving}
          >
            {t("Assign selected metrics")}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-6">
          <div className="xl:col-span-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
              {t("Reporting Year")}
            </p>
            {reportingYearsLoading ? (
              <Skeleton className="h-9 w-full rounded-md" />
            ) : (
              <SearchableSelect
                value={selectedReportingYear?._id || ""}
                onChange={handleReportingYearChange}
                options={reportingYearOptions}
                placeholder={t("Select Reporting Year")}
                searchPlaceholder={t("Search Reporting Year")}
                maxLabelLength={30}
              />
            )}
          </div>

          <div className="xl:col-span-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
              {t("Frameworks")}
            </p>
            {frameworksLoading || reportingYearsLoading ? (
              <Skeleton className="h-9 w-full rounded-md" />
            ) : (
              <SearchableMultiSelect
                value={frameworks}
                onChange={setFrameworks}
                options={frameworkOptions}
                placeholder={t("All frameworks")}
                searchPlaceholder={t("Search frameworks")}
                maxLabelLength={15}
                disabled={
                  !frameworkSelectionReady || frameworkOptions.length === 0
                }
              />
            )}
          </div>
          {/* Search Box */}
          <div className="xl:col-span-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
              {t("Search")}
            </p>
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("Search by code or title ... ")}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-muted-foreground text-sm">
              {t("Pick relevant E/S/G metrics for master data")}
            </p>
            <Badge variant="outline">
              {selectedCount} {t("selected")}
            </Badge>
          </div>
        </div>

        {initialDisclosuresLoading ? (
          <AssignDisclosureGridSkeleton />
        ) : (
          <div
            className={`grid grid-cols-1 gap-4 flex-1 min-h-0 overflow-hidden ${categoryGridClass}`}
          >
            {categoryNames.map((category, index) => {
              return (
                <AssignDisclosureSelectionCard
                  category={category}
                  disclosures={groupedByCategory[category] || []}
                  selectedDisclosureIds={selectedDisclosureIds}
                  disclosuresLoading={loadingAllDisclosures}
                  onToggleDisclosure={toggleDisclosureSelection}
                  onSelectAllByCategory={selectAllByCategory}
                  onUnselectAllByCategory={unselectAllByCategory}
                  getFrameworkLabel={getFrameworkLabel}
                  t={t}
                  key={index}
                />
              );
            })}
          </div>
        )}
      </div>

      <AssignMasterDataDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        generalOnly={hasGeneralDisclosure}
        onSubmit={() => {
          setDialogOpen(false);
          handleSave();
        }}
        selectedReportingYear={selectedReportingYear}
        reportingYearOrganizationIds={reportingYearOrganizationIds}
        year={year}
        setYear={setYear}
        organizationMode={organizationMode}
        setOrganizationMode={setOrganizationMode}
        selectedOrganizations={selectedOrganizations}
        setSelectedOrganizations={setSelectedOrganizations}
        organizationTree={reportingYearOrganizationTree}
        organizationLoading={organizationLoading}
        saving={saving}
      />
    </div>
  );
});

export default AssignMasterData;
